import { Col, Row, Spin, Tooltip } from "antd";
import React, { useEffect, useState } from "react";
import _isEmpty from "lodash/isEmpty";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import CustomIcon from "../../components/CustomIcon/CustomIcon";
import HomeTotalAndPercentageBox from "../../components/HomeTotalAndPercentageBox/HomeTotalAndPercentageBox";
import FilterPropertyHorizontal from "../FilterPropertyHorizontal/FilterPropertyHorizontal";
import PageTitle from "../../components/PageTitle/PageTitle";
import { useHomePagination, useLanguage, useHomeFilterTags } from "../../hooks";
import { getAnnouncement } from "../../store/actions/announcement.action";
import {
  ANNOUNCEMENT_ID,
  HELPER_TEXT_KEYS,
  HOME_PROPERTIES_TYPE,
  ROUTES,
  PAGINATOIN,
  TAG,
  DEFAULT_PROPERTY_SORTER,
  SHOW_LAND_READY_FOR_PUBLISHING_SECTION,
} from "../../utils/constants";
import permissionsUtil from "../../utils/permissions.util";
import { getDangerouslySetHTML, getTranslatedHelperText, getFeatureFlags } from "../../utils/utils";
import Announcements from "../Announcements/Announcements";

import "./Home.scss";
import NewStatsSummary from "../../components/NewStatsSummary/NewStatsSummary";
import PropertyCountBox from "../../components/PropertyCountBox/PropertyCountBox";

const Home = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const history = useHistory();
  const { language } = useLanguage();

  const {
    unitsToReview,
    activeAudits,
    marketableUnits,
    marketableUnitsCount,
    publishedSummary,
    readyForPublishSummary,
    totalMarketableUnits,
    unitsReadyForPublishing,
    land,
    readyForReviewAuditSummary,
  } = useSelector(state => state.home);
  const { value: announcement } = useSelector(state => state.announcement);
  const { lookupList } = useSelector(state => state.helperText);

  const { homePagination, homeTags } = useSelector(state => state.home);
  const { homeFilterPagination } = useHomePagination({
    pagination: homePagination,
    paginationType: PAGINATOIN.home,
  });
  const { homeFilterTag } = useHomeFilterTags({ tags: homeTags, tagType: TAG.home });

  const canAccessAdmin = permissionsUtil.checkAuth("user_view");

  const [isLoading, setIsLoading] = useState(false);
  const [shouldFetchData, setShouldFetchData] = useState(false);

  const FEATURE_FLAG = getFeatureFlags();
  const { USE_READY_FOR_MARKETING_FLAG } = FEATURE_FLAG;
  const isRfmEnabled = Boolean(USE_READY_FOR_MARKETING_FLAG?.status);

  useEffect(() => {
    getAllDashboardData();
  }, []);

  const getAllDashboardData = async () => {
    try {
      setIsLoading(true);
      await dispatch(getAnnouncement(ANNOUNCEMENT_ID));
    } catch (ex) {
      console.log("ex", ex);
    }
  };

  const redirectToPublish = type => {
    if (!isRfmEnabled && [HOME_PROPERTIES_TYPE.auditReadyForReview, HOME_PROPERTIES_TYPE.auditIssues].includes(type)) {
      history.push(`${ROUTES.AUDIT.path}`);
      return;
    }

    if (type === HOME_PROPERTIES_TYPE.openAuditIssues) {
      history.push(`${ROUTES.OPEN_AUDIT_ISSUES.path}/${type}`);
    } else if (type === HOME_PROPERTIES_TYPE.auditReadyForReview) {
      history.push(`${ROUTES.AUDIT_READY_FOR_REVIEW.path}`);
    } else if (type === HOME_PROPERTIES_TYPE.auditIssues) {
      history.push(`${ROUTES.AUDIT_ISSUES.path}`);
    } else if (type === HOME_PROPERTIES_TYPE.landReadyForPublishing) {
      history.push(`${ROUTES.PROPERTIES.path}`);
    } else {
      history.push(`${ROUTES.PROPERTIES_TO_REVIEW.path}/${type}`);
    }
  };

  const redirectToNotMarket = type => {
    history.push(`${ROUTES.UNITS_TO_REVIEW.path}/${type}`);
  };

  return (
    <>
      {isLoading ? (
        <div className="edit-form__spin">
          <Spin size="large" tip={t("ErrorMessages.Loading")} />
        </div>
      ) : null}
      <div className="home">
        {(canAccessAdmin && <Announcements />) || (announcement?.isEnable && <Announcements />)}
        <PageTitle title={ROUTES.HOME.title} />
        <Row justify="center">
          <Col xs={24} className="home-dropdowns">
            <FilterPropertyHorizontal
              defaultSort={DEFAULT_PROPERTY_SORTER}
              shouldFetchData={shouldFetchData}
              homePagination={homeFilterPagination}
              homeTags={homeFilterTag}
              isRfmEnabled={isRfmEnabled}
              setIsLoading={setIsLoading}
            />
          </Col>
          {!isRfmEnabled && (
            <>
              <Col xs={24} md={24} xl={24} xxl={18} className="total-units">
                <Row justify="space-between" className="units-details">
                  <Col xs={24} md={12} xl={12} xxl={12} align="center">
                    <p className="font-w-500 font-20">
                      {t("Home.UnitsToReview")}
                      {!_isEmpty(lookupList[HELPER_TEXT_KEYS.home.unitstoReview]) && (
                        <span className="font-14 font-w-400 mg-left-6">
                          <Tooltip
                            title={getDangerouslySetHTML(
                              getTranslatedHelperText(lookupList[HELPER_TEXT_KEYS.home.unitstoReview], language),
                            )}
                          >
                            <CustomIcon name="InfoCircleOutlined" />
                          </Tooltip>
                        </span>
                      )}
                    </p>
                    <span className="font-bold font-40">{unitsToReview?.totalUnitsToReview || "0"}</span>
                  </Col>
                  <Col xs={24} md={12} xl={12} xxl={12} align="center">
                    <p className="font-w-500 font-20 pad-left-120 pad-right-120 line-h-24 mg-bottom-4">
                      {t("Home.MarketableUnits")}
                      {!_isEmpty(lookupList[HELPER_TEXT_KEYS.home.marketableUnitsNotOnPropertySearch]) && (
                        <span className="font-14 font-w-400 mg-left-6">
                          <Tooltip
                            title={getDangerouslySetHTML(
                              getTranslatedHelperText(
                                lookupList[HELPER_TEXT_KEYS.home.marketableUnitsNotOnPropertySearch],
                                language,
                              ),
                            )}
                          >
                            <CustomIcon name="InfoCircleOutlined" />
                          </Tooltip>
                        </span>
                      )}
                      <br />
                      {t("Home.PropertySearch")}
                    </p>
                    <div className="ant-col ant-col-xs-24 ant-col-md-12 ant-col-xl-12 ant-col-xxl-12">
                      <div className="container">
                        <div className="mg-bottom-1">
                          <h4>Total</h4>
                          <span className="font-bold font-40 color-black">
                            {(marketableUnitsCount && `${parseFloat(marketableUnitsCount).toFixed(0)}`) || "0"}
                          </span>
                        </div>
                        <div className="mg-bottom-1">
                          <h4>Percent</h4>
                          <span className="font-bold font-40 color-black">
                            {(marketableUnits && `${parseFloat(marketableUnits).toFixed(0)}%`) || "0"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>
              </Col>

              <Col xs={24} md={20} xl={24} xxl={18} className="mg-top-12">
                <Row>
                  <Col xs={24} md={24} xl={12} xxl={12} className="vertical-divider">
                    <HomeTotalAndPercentageBox
                      title={t("Home.CurrentVacantUnits")}
                      total={unitsToReview?.totalVacantUnits}
                      percent={unitsToReview?.vacantPercentage}
                      actionText={t("Nav.Publish")}
                      secActionText={t("Buttons.Unmarket")}
                      type={HOME_PROPERTIES_TYPE.vacantProperties}
                      redirectToPublish={redirectToPublish}
                      redirectToNotMarket={redirectToNotMarket}
                      tooltip={lookupList[HELPER_TEXT_KEYS.home.currentVacantUnits]}
                    />
                    <HomeTotalAndPercentageBox
                      title={t("Home.UpcomingVacantUnits")}
                      total={unitsToReview?.availableInYear}
                      percent={unitsToReview?.availableInYearPercentage}
                      actionText={t("Nav.Publish")}
                      type={HOME_PROPERTIES_TYPE.upcomingProperties}
                      secActionText={t("Buttons.Unmarket")}
                      redirectToPublish={redirectToPublish}
                      redirectToNotMarket={redirectToNotMarket}
                      tooltip={lookupList[HELPER_TEXT_KEYS.home.upcomingVacantUnits]}
                    />
                  </Col>
                  <Col xs={24} md={24} xl={12} xxl={12}>
                    <HomeTotalAndPercentageBox
                      title={t("Home.NotMarketingOngoingReview")}
                      total={unitsToReview?.totalUnmarketUnits}
                      percent={unitsToReview?.unMarketedPercentage}
                      actionText={t("Nav.Publish")}
                      secActionText={t("Buttons.Unmarket")}
                      type={HOME_PROPERTIES_TYPE.unmnarketedProperties}
                      redirectToPublish={redirectToPublish}
                      redirectToNotMarket={redirectToNotMarket}
                      tooltip={lookupList[HELPER_TEXT_KEYS.home.notMarketingOngoingReview]}
                    />
                    <HomeTotalAndPercentageBox
                      title={t("Home.UnitsLandActiveAudits")}
                      total={activeAudits?.totalopenAudits}
                      actionText={t("Home.ViewAll")}
                      type={HOME_PROPERTIES_TYPE.openAuditIssues}
                      redirectToPublish={redirectToPublish}
                      redirectToNotMarket={redirectToNotMarket}
                      tooltip={lookupList[HELPER_TEXT_KEYS.home.propertiesActiveAudits]}
                    />
                  </Col>
                </Row>
              </Col>
            </>
          )}
          {isRfmEnabled && (
            <>
              <Col xs={24} md={24} xl={24} lg={24} xxl={20}>
                <NewStatsSummary
                  ready={{
                    label: "Ready for Publishing",
                    value: readyForPublishSummary?.readyForPublishCount || 0,
                    sub: `${readyForPublishSummary?.readyForPublishPercent || 0}%`,
                  }}
                  published={{
                    label: "Published",
                    value: publishedSummary?.publishedCount || 0,
                    sub: `${publishedSummary?.publishedPercent || 0}%`,
                  }}
                  total={{
                    label: "Total Marketable Units",
                    value: totalMarketableUnits || 0,
                    tooltip: "Sum of all Units that have 'Ready for Marketing' flag",
                  }}
                />
              </Col>

              <Col xs={24} md={24} xl={24} lg={24} xxl={20}>
                <Row>
                  <Col xs={24} md={24} xl={12} xxl={12} lg={12}>
                    <PropertyCountBox
                      title="Units Ready for Publishing"
                      properties={[
                        {
                          name: "Vacant Units",
                          total: unitsReadyForPublishing?.currentvacantUnits || 0,
                          percentage: `${unitsReadyForPublishing?.currentVacantUnitsPercent || 0}%`,
                          type: HOME_PROPERTIES_TYPE.vacantProperties,
                          redirectToPublish,
                        },
                        {
                          name: "Upcoming Vacant Units",
                          total: unitsReadyForPublishing?.upcomingVacantUnits || 0,
                          percentage: `${unitsReadyForPublishing?.upcomingVacantUnitsPercent || 0}%`,
                          type: HOME_PROPERTIES_TYPE.upcomingProperties,
                          redirectToPublish,
                        },
                      ]}
                      actionText="Publish"
                    />
                    {SHOW_LAND_READY_FOR_PUBLISHING_SECTION && (
                      <PropertyCountBox
                        title="Land Ready for Publishing"
                        properties={[
                          {
                            name: "Land",
                            total: land?.marketable?.count || 0,
                            percentage: `${land?.marketable?.percent || 0}%`,
                            type: HOME_PROPERTIES_TYPE.landReadyForPublishing,
                            redirectToPublish,
                            disabledReason: "No marketable Land.",
                          },
                        ]}
                        actionText="Publish"
                      />
                    )}
                  </Col>
                  <Col xs={24} md={24} xl={12} xxl={12} lg={12}>
                    <PropertyCountBox
                      title="Audits"
                      properties={[
                        {
                          name: "Ready for Review",
                          total: readyForReviewAuditSummary?.readyForReviewAudits || 0,
                          type: HOME_PROPERTIES_TYPE.auditReadyForReview,
                          redirectToPublish,
                          actionText: "Review",
                          disabledReason: "No audit items",
                        },
                        {
                          name: "Audit Issues",
                          total: readyForReviewAuditSummary?.auditIssues || 0,
                          type: HOME_PROPERTIES_TYPE.auditIssues,
                          redirectToPublish,
                          actionText: "View",
                          disabledReason: "No audit items",
                        },
                      ]}
                    />
                  </Col>
                </Row>
              </Col>
            </>
          )}
        </Row>
      </div>
    </>
  );
};

Home.propTypes = {};

export default Home;
